// Forms a single entry point for loading rules
// Rules can contain a schedule/meta data and in the future triggers/conditions
import { isUndefined } from '../../lib/typeCheck'

// External modules
import { batch } from 'react-redux';
import moment from 'moment';
import { useSelector } from 'react-redux';

// Store
import { AppThunk, RootState } from '../store';
import { NewSchedule, EditRule } from './types';
import { prepareRulesForStore, loadRules, loadSavedRule } from './rulesSlice';
import { prepareRuleSchedulesForStore, loadRuleSchedules, loadSavedSchedule } from './ruleSchedulesSlice';
import { cloneDeep } from 'lodash';
import { getOverlapWarning, addWarning } from '../common/warningSlice';

// API calls
import { getRules, saveRule } from '../../api/rules';
import { getSchedules, saveSchedule } from '../../api/ruleSchedules';
import { CalendarItems } from '../../lib/calendar-items';
import { checkOverlapping } from '../../lib/overlap';

import { WarningSlice } from '../common/types';

export const createCompleteRule = (newSchedule: NewSchedule, newRule: EditRule, selectedNodes, calendar_items_recurring?: Array<any>, warning?: WarningSlice): AppThunk => async (dispatch, getState) => {
  const { user, menuNodes } = getState();
  
  try {
    // const warning = useSelector(getOverlapWarning);
    let weekstart = moment().startOf('isoWeek').subtract(1, 'week');
    let back_dated_start = weekstart.hour(parseInt(moment(newSchedule.dtstart).format('H')));
    
    // Temporary create an id to identify overlapping schedules
    let id = null;
    if (calendar_items_recurring.length) {
      id = calendar_items_recurring[calendar_items_recurring.length - 1].id + 1;
    }
    
    const schedule = {
      id:id,
      schedules_id: null,
      status: 1,
      title: newSchedule.title,
      description: '',
      freq: 'WEEKLY',
      dtstart: back_dated_start.format('YYYY-MM-DD HH:mm:ss'),
      duration: newSchedule.duration,
      interval: 1,
      byweekday: newSchedule.byweekday,
      byminute: isUndefined(newSchedule.byminute)? null : newSchedule.byminute,
      byhour: isUndefined(newSchedule.byminute)? null : newSchedule.byhour,
    }
    
    // Format the new schedule and add that to the 'calendar_items_recurring' array
    calendar_items_recurring = CalendarItems(schedule, calendar_items_recurring);
    // Identifies overlapping schedules
    const isOverlapping = checkOverlapping(calendar_items_recurring);
    
    if (!isOverlapping.state){
      // If there aren't any overlapping schedules
      // Remove the temporarily created id
      delete schedule['id'];
      const scheduleResult = await saveSchedule(user.api_key, user.account.id, schedule);  
          
      const ruleResult = await saveRule(user.api_key, user.account.id, {
        rule_id: null,
        station_id: parseInt(selectedNodes[0].id),
        schedules_id: scheduleResult.id,
        category: newRule.category,
        title: newRule.title,
        description: '',
        status: 1,
        meta: newRule.meta,
        wrx_option_id: newRule.wrx_option_id
      });    
        batch(() => {
        dispatch(loadSavedSchedule({ savedSchedule: scheduleResult, selectedNodes: selectedNodes }));
        dispatch(loadSavedRule({ savedRule: ruleResult, selectedNodes: selectedNodes }));
      })
    }
    else{
      
      // If there are any overlapping schedules
      // Identify the newly added schedule index
      let removableIndex = calendar_items_recurring.findIndex(p => p.id === id);    

      // Remove them from the `calendar_items_recurring` 
      calendar_items_recurring.splice(removableIndex, 2);
      
      // Set all item.overlapping to false, inorder to clear the history
      calendar_items_recurring.forEach((item) => {
        item.overlapping = false;      
      })

      // Clear `isOverlapping` object
      isOverlapping.state = false;
      isOverlapping.schedules = [];  
      
      const tab_type = newRule.category;
           
      // If this a brand new schedule that overlaps, set is_exisitng to false
      let is_existing = false;

      // If there is an existing overlap already, set is_exiting as to true
      if (warning.existing) is_existing = warning.existing

      let overlap = {};
      switch(newRule.category){
        case 'power':
          overlap = { power: true }
          break;
        
        case 'rawaudio':
          overlap = { rawaudio: true }
          break;

        case 'quiet':
          overlap = { quiet_periods: true }
          break;

        default: 
          overlap = { 
            power: false,
            rawaudio: false,
            quiet_periods: false }
          break;
      }
      
      // Update the store to alert the user
      dispatch(addWarning({id: parseInt(selectedNodes[0].id), overlap: overlap, attempt: warning.attempt + 1, existing: is_existing}));      
    }    
    
  } catch (e) {
    console.log(e)
  }
}

export const fetchResolvedRules = (): AppThunk => async (dispatch, getState) => {
  const { user, menuNodes } = getState();
  
  let stationIds = menuNodes.filter((node) => node.selected && node.type == 'station').map(node => parseInt(node.id))
  if (stationIds.length !== 1) {
    return;
  }
  
  try {
    const rules = await getRules(user.api_key, stationIds, 'all');
    const ruleSchedules = await getSchedules(user.api_key, stationIds, 'all');
    
    batch(() => {
      dispatch(loadRules(prepareRulesForStore(rules)));
      dispatch(loadRuleSchedules(prepareRuleSchedulesForStore(ruleSchedules)));
    })
    
  } catch (e) {
    console.error(e)
  }
}

export const selectFilteredResolvedRules = (category: string) => (state: RootState) => {
  const resolvedRules = state.rules.map(rule => {
    const resolvedSchedule = cloneDeep(state.ruleSchedules.filter(ruleSchedule => ruleSchedule.schedules_id === rule.schedules_id)[0]) || null;
        
    return {
      ...rule,
      resolvedSchedule,
    }
  })
  .filter(rule => rule.resolvedSchedule !== null)
  
  if (category === 'all') return resolvedRules;
  
  if (category === 'rawaudio') {
    return resolvedRules.filter(resolvedRule => resolvedRule.category === category && resolvedRule.resolvedSchedule.byhour !== null && resolvedRule.resolvedSchedule.byminute !== null);
  }
  
  return resolvedRules.filter(resolvedRule => resolvedRule.category === category);
}